import {
    Box, Button, Typography, TextField, MenuItem
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import operatorMasterService from "../../../services/masters/operatorMaster.Service";
import plantMasterService from "../../../services/masters/plantMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

// ── Styles (mirrors PlantAdd exactly) ────────────────────────────────────────
const pageSx = {
    background: "#F4F7FB",
    minHeight: "100vh",
    padding: "20px 24px 40px",
    fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
    fontSize: "12px",
    color: "#718096",
    mb: 2,
    display: "flex",
    alignItems: "center",
    gap: "6px",
};

const pageHeaderSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2.5,
};

const pageTitleSx = {
    fontSize: "16px",
    fontWeight: 700,
    color: "#000000",
    display: "flex",
    alignItems: "center",
    gap: "10px",
};

const backBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#2B6CB0",
    padding: "7px 14px",
    border: "1.5px solid #2B6CB0",
    borderRadius: "7px",
    background: "#fff",
    cursor: "pointer",
    textTransform: "none",
    "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
    background: "#fff",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
};

const sectionHeaderSx = {
    background: "#1C3A5E",
    padding: "12px 22px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
};

const sectionHeaderTitleSx = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
    fontSize: "12px",
    fontWeight: 600,
    color: "#2063b6",
    mb: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
};

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        fontSize: "13px",
        fontFamily: "'Inter', Arial, sans-serif",
        background: "#fafafa",
        height: "38px",
        "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
        "&:hover fieldset": { borderColor: "#CBD5E0" },
        "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
        "&.Mui-focused": { background: "#fff" },
        "&.Mui-disabled": { background: "#F8FAFC" },
    },
    "& input": {
        color: "#1A202C",
        fontSize: "13px",
        fontFamily: "'Inter', Arial, sans-serif",
        "&::placeholder": { color: "#A0AEC0", opacity: 1 },
    },
    "& .MuiInputLabel-root": { display: "none" },
};

const menuItemSx = { fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" };

const formFooterSx = {
    padding: "18px 24px",
    background: "#F8FAFC",
    borderTop: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
};

const saveBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 22px",
    height: "40px",
    background: "#4CAF50",
    color: "#fff",
    border: "none",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
    "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
    transition: "all 0.15s ease",
};

const cancelBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 22px",
    height: "40px",
    background: "#E53E3E",
    color: "#ffffff",
    border: "none",
    "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
    fontSize: "14px",
    fontWeight: 600,
    borderRadius: "8px",
    textTransform: "none",
};
// ─────────────────────────────────────────────────────────────────────────────

const OperatorAdd = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const urlLocation = useLocation();
    const editData = urlLocation?.state;

    const login_details = sessionStorage.getItem('loginUser');
    const userD = JSON.parse(login_details);
    const userInfo = userD.result.userInfo;

    const [plantList, setPlantList] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [data, setData] = useState({
        operator_name: '',
        plant_id: '',
        operator_id: '',
    });

    // ── Fetch plant list for dropdown ────────────────────────────────────────
    useEffect(() => {
        const fetchPlants = async () => {
            try {
                const res = await plantMasterService.getPlantList({
                    page: 0, size: 999, searchKey: ''
                });
                setPlantList(Array.isArray(res) ? res : []);
            } catch (err) {
                console.error('Error fetching plants:', err);
            }
        };
        fetchPlants();
    }, []);

    // ── Pre-fill when editing ────────────────────────────────────────────────
    useEffect(() => {
        if (editData) {
            setData({
                operator_name: editData.operator_name || '',
                plant_id: editData.plant_id || '',
                operator_id: editData.operator_id || '',
            });
        }
    }, [editData]);

    const handleChange = ({ target }) => {
        const { name, value } = target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        if (!data.operator_name || !data.plant_id) {
            setSuccessMessage("Operator name and plant are required.");
            setOpenSnackbar(true);
            return false;
        }
        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = {
            ...data,
            modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            modified_by: editData ? userInfo.user_id : null,
            created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            created_by: editData ? editData.created_by : userInfo.user_id,
            changedUpdatedValue: 'edit',
        };

        try {
            if (editData) {
                await operatorMasterService.editDeleteOperator(payload);
            } else {
                await operatorMasterService.addOperator(payload);
            }
            setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
            setOpenSnackbar(true);
            setTimeout(() => navigate(-1), 1200);
        } catch (error) {
            console.error("Submission error:", error);
            setSuccessMessage("Error occurred while saving data.");
            setOpenSnackbar(true);
        }
    };

    return (
        <Box sx={pageSx}>
            {/* Breadcrumb */}
            <Box sx={breadcrumbSx}>
                <span>Dashboard</span> /
                <span
                    onClick={() => navigate('/masters/MastersMain/Masters')}
                    style={{ cursor: 'pointer' }}
                >
                    Masters
                </span> /
                <span style={{ color: "#2B6CB0", fontWeight: 600 }}>
                    {editData ? 'Edit Operator' : 'Add Operator'}
                </span>
            </Box>

            {/* Page Header */}
            <Box sx={pageHeaderSx}>
                <Box sx={pageTitleSx}>
                    <PeopleAltIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
                    {editData ? 'UPDATE' : 'ADD'} OPERATOR MASTER
                </Box>
                <Button onClick={() => navigate(-1)} sx={backBtnSx}>
                    <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
                </Button>
            </Box>

            {/* Form Card */}
            <Box sx={formCardSx}>
                {/* Navy Strip */}
                <Box sx={sectionHeaderSx}>
                    <PeopleAltIcon sx={{ fontSize: 18, color: "#fff" }} />
                    <Typography sx={sectionHeaderTitleSx}>Operator Master</Typography>
                </Box>

                <form onSubmit={onSubmit}>
                    <Box sx={formBodySx}>
                        <Grid container spacing={3}>

                            {/* Operator Name */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Operator Name
                                        <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="operator_name"
                                        value={data.operator_name}
                                        onChange={handleChange}
                                        sx={inputSx}
                                        placeholder="Enter operator name"
                                        required
                                    />
                                </Box>
                            </Grid>

                            {/* Plant Dropdown */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Plant
                                        <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        name="plant_id"
                                        value={data.plant_id}
                                        onChange={handleChange}
                                        sx={inputSx}
                                        required
                                    >
                                        <MenuItem value="" sx={menuItemSx}>
                                            <em>Select Plant</em>
                                        </MenuItem>
                                        {plantList.map((plant) => (
                                            <MenuItem
                                                key={plant.plant_id}
                                                value={plant.plant_id}
                                                sx={menuItemSx}
                                            >
                                                {plant.plant_name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>

                    {/* Footer */}
                    <Box sx={formFooterSx}>
                        <Button sx={cancelBtnSx} onClick={() => navigate(-1)}>
                            Cancel
                        </Button>
                        <Button type="submit" sx={saveBtnSx}>
                            <SaveIcon sx={{ fontSize: 18 }} />
                            {editData ? "Update" : "Save"}
                        </Button>
                    </Box>
                </form>
            </Box>

            <ReusableSnackbar
                open={openSnackbar}
                onClose={(e, reason) => {
                    if (reason !== 'clickaway') setOpenSnackbar(false);
                }}
                message={successMessage}
                autoHideDuration={2000}
                vertical="top"
                horizontal="right"
            />
        </Box>
    );
};

export default OperatorAdd;